/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// custom.js
//console.log("Custom.js loaded, waiting for Shopify form embed...");

/* Mappa completa dei paesi più comuni */
const countryMap = {
  "Afghanistan": "AF","Albania": "AL","Algeria": "DZ","Andorra": "AD","Angola": "AO",
  "Antigua and Barbuda": "AG","Argentina": "AR","Armenia": "AM","Australia": "AU","Austria": "AT",
  "Azerbaijan": "AZ","Bahamas": "BS","Bahrain": "BH","Bangladesh": "BD","Barbados": "BB",
  "Belarus": "BY","Belgium": "BE","Belize": "BZ","Benin": "BJ","Bhutan": "BT",
  "Bolivia": "BO","Bosnia and Herzegovina": "BA","Botswana": "BW","Brazil": "BR","Brunei": "BN",
  "Bulgaria": "BG","Burkina Faso": "BF","Burundi": "BI","Cabo Verde": "CV","Cambodia": "KH",
  "Cameroon": "CM","Canada": "CA","Central African Republic": "CF","Chad": "TD","Chile": "CL",
  "China": "CN","Colombia": "CO","Comoros": "KM","Congo (Congo-Brazzaville)": "CG","Costa Rica": "CR",
  "Croatia": "HR","Cuba": "CU","Cyprus": "CY","Czechia (Czech Republic)": "CZ","Denmark": "DK",
  "Djibouti": "DJ","Dominica": "DM","Dominican Republic": "DO","Ecuador": "EC","Egypt": "EG",
  "El Salvador": "SV","Equatorial Guinea": "GQ","Eritrea": "ER","Estonia": "EE","Eswatini": "SZ",
  "Ethiopia": "ET","Fiji": "FJ","Finland": "FI","France": "FR","Gabon": "GA",
  "Gambia": "GM","Georgia": "GE","Germany": "DE","Ghana": "GH","Greece": "GR",
  "Grenada": "GD","Guatemala": "GT","Guinea": "GN","Guinea-Bissau": "GW","Guyana": "GY",
  "Haiti": "HT","Honduras": "HN","Hungary": "HU","Iceland": "IS","India": "IN",
  "Indonesia": "ID","Iran": "IR","Iraq": "IQ","Ireland": "IE","Israel": "IL",
  "Italy": "IT","Jamaica": "JM","Japan": "JP","Jordan": "JO","Kazakhstan": "KZ",
  "Kenya": "KE","Kiribati": "KI","Kuwait": "KW","Kyrgyzstan": "KG","Laos": "LA",
  "Latvia": "LV","Lebanon": "LB","Lesotho": "LS","Liberia": "LR","Libya": "LY",
  "Liechtenstein": "LI","Lithuania": "LT","Luxembourg": "LU","Madagascar": "MG","Malawi": "MW",
  "Malaysia": "MY","Maldives": "MV","Mali": "ML","Malta": "MT","Marshall Islands": "MH",
  "Mauritania": "MR","Mauritius": "MU","Mexico": "MX","Micronesia": "FM","Moldova": "MD",
  "Monaco": "MC","Mongolia": "MN","Montenegro": "ME","Morocco": "MA","Mozambique": "MZ",
  "Myanmar": "MM","Namibia": "NA","Nauru": "NR","Nepal": "NP","Netherlands": "NL",
  "New Zealand": "NZ","Nicaragua": "NI","Niger": "NE","Nigeria": "NG","North Korea": "KP",
  "North Macedonia": "MK","Norway": "NO","Oman": "OM","Pakistan": "PK","Palau": "PW",
  "Palestine State": "PS","Panama": "PA","Papua New Guinea": "PG","Paraguay": "PY","Peru": "PE",
  "Philippines": "PH","Poland": "PL","Portugal": "PT","Qatar": "QA","Romania": "RO",
  "Russia": "RU","Rwanda": "RW","Saint Kitts and Nevis": "KN","Saint Lucia": "LC","Saint Vincent and the Grenadines": "VC",
  "Samoa": "WS","San Marino": "SM","Sao Tome and Principe": "ST","Saudi Arabia": "SA","Senegal": "SN",
  "Serbia": "RS","Seychelles": "SC","Sierra Leone": "SL","Singapore": "SG","Slovakia": "SK",
  "Slovenia": "SI","Solomon Islands": "SB","Somalia": "SO","South Africa": "ZA","South Korea": "KR",
  "South Sudan": "SS","Spain": "ES","Sri Lanka": "LK","Sudan": "SD","Suriname": "SR",
  "Sweden": "SE","Switzerland": "CH","Syria": "SY","Taiwan": "TW","Tajikistan": "TJ",
  "Tanzania": "TZ","Thailand": "TH","Timor-Leste": "TL","Togo": "TG","Tonga": "TO",
  "Trinidad and Tobago": "TT","Tunisia": "TN","Turkey": "TR","Turkmenistan": "TM","Tuvalu": "TV",
  "Uganda": "UG","Ukraine": "UA","United Arab Emirates": "AE","United Kingdom": "GB","United States": "US",
  "Uruguay": "UY","Uzbekistan": "UZ","Vanuatu": "VU","Vatican City": "VA","Venezuela": "VE",
  "Vietnam": "VN","Yemen": "YE","Zambia": "ZM","Zimbabwe": "ZW"
};

// Aspetta che lo Shopify form embed sia pronto
function waitForEmbed(callback) {
  const interval = setInterval(() => {
    const embed = document.querySelector("shopify-forms-embed");
    if (embed) {
      clearInterval(interval);
      console.log("Shopify form embed found!");
      callback(embed);
    }
  }, 500);
}

// Inizializza il form
function initForm(embed) {
  const shadowRoot = embed.shadowRoot;
  if (!shadowRoot) {
    console.warn("Shadow root not ready, retry in 500ms...");
    setTimeout(() => initForm(embed), 500);
    return;
  }

  const form = shadowRoot.querySelector("form[data-testid='form']");
  if (!form) {
    console.warn("Form not yet in shadow root, retry in 500ms...");
    setTimeout(() => initForm(embed), 500);
    return;
  }

  console.log("Form found inside shadow root!");

  // Trova il campo Country of destination
  const countryContainer = Array.from(shadowRoot.querySelectorAll("._selectContainer_12thd_6"))
    .find(div => div.querySelector("._label_12thd_10")?.textContent.trim() === "Country of destination");

  if (!countryContainer) return console.warn("Country of destination container not found!");

  const countrySpan = countryContainer.querySelector("._selectToggleText_12thd_25");
  if (!countrySpan) return console.warn("Country span not found!");

  // Aspetta che Shopify renda il suo campo countrycode, poi aggiorna quello direttamente
  function waitForCountryInput(callback) {
    const input = shadowRoot.querySelector("input[id='custom#country_code']");
    if (input) {
      callback(input);
    } else {
      setTimeout(() => waitForCountryInput(callback), 500);
    }
  }

  // Setter nativo necessario per aggirare React/Preact che controlla l'input
  const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;

  waitForCountryInput((countryInput) => {
    // Nascondi il container del campo
    const container = countryInput.closest('._formFieldContainer_1mxsl_5');
    if (container) {
      container.style.display = 'none';
      console.log('Campo countrycode nascosto correttamente!');
    }

    // Aggiorna il country code nel campo Shopify (via setter nativo + eventi per React)
    const updateCountryCode = () => {
      const countryName = countrySpan.textContent.trim();
      const code = countryMap[countryName] || "";
      nativeSetter.call(countryInput, code);
      countryInput.dispatchEvent(new Event('input', { bubbles: true }));
      countryInput.dispatchEvent(new Event('change', { bubbles: true }));
      console.log("Selected country:", countryName, "-> ISO code:", code);
    };

    updateCountryCode();

    new MutationObserver(updateCountryCode).observe(countrySpan.parentElement, {
      subtree: true,
      characterData: true,
      childList: true
    });

    console.log("Country code binding and hiding ready!");
  });
}

// Avvia
waitForEmbed(initForm);

/**
 * Autofill per il form Shopify "shop-lead-capture" (embed: <shopify-forms-embed>).
 * Compila: company_name, first_name, last_name, email, phone.
 * I campi compilati vengono impostati come "readonly": l'utente non può modificarli,
 * ma il valore viene comunque incluso nel submit (a differenza di "disabled").
 *
 * Richiede in pagina (via Liquid), solo se il customer è loggato:
 *
 *   {% if customer %}
 *     {% assign addr = customer.default_address %}
 *     <script type="application/json" id="customer-data">
 *       {
 *         "first_name": {{ customer.first_name | json }},
 *         "last_name": {{ customer.last_name | json }},
 *         "email": {{ customer.email | json }},
 *         "phone": {{ customer.phone | default: addr.phone | json }},
 *         "company": {{ customer.current_company.name | default: addr.company | json }}
 *       }
 *     </script>
 *   {% elsif request.design_mode %}
 *     {% comment %} Nessun redirect/contenuto in editor mode {% endcomment %}
 *   {% else %}
 *     <script>window.location.href = "/";</script>
 *   {% endif %}
 *
 * LIMITI NOTI:
 * - Se lo shadow root è "closed" non è raggiungibile da JS esterno (limite del browser).
 * - Se i selettori CSS (#company_name, #first_name, ecc.) cambiano con un aggiornamento
 *   del tema Shopify, vanno riverificati da devtools.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // 1. Lettura dati customer dal JSON iniettato via Liquid
  // ---------------------------------------------------------------------
  function readCustomerData() {
    const el = document.getElementById('customer-data');
    if (!el) {
      console.warn('[autofill] #customer-data non trovato (utente non loggato?)');
      return {};
    }
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[autofill] JSON non valido in #customer-data', e);
      return {};
    }
  }

  const customer = readCustomerData();

  const userData = {
    company_name: customer.company || '',
    first_name: customer.first_name || '',
    last_name: customer.last_name || '',
    email: customer.email || '',
    phone: customer.phone || '',
  };

  const hasData = Object.values(userData).some(Boolean);
  if (!hasData) {
    console.log('[autofill] nessun dato customer disponibile, autofill saltato');
    return;
  }

  // ---------------------------------------------------------------------
  // 2. Setter nativo per bypassare i controlled-component React
  // ---------------------------------------------------------------------
  const NATIVE_INPUT_SETTER = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;

  function setNativeValue(el, value) {
    NATIVE_INPUT_SETTER.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    el.dispatchEvent(new Event('blur', { bubbles: true }));
  }

  function fillField(shadowRoot, id, value) {
    if (!value) return;
    const el = shadowRoot.querySelector(`#${CSS.escape(id)}`);
    if (!el) {
      console.warn(`[autofill] campo non trovato: ${id}`);
      return;
    }
    setNativeValue(el, value);
    el.setAttribute('readonly', 'readonly');
  }

  // ---------------------------------------------------------------------
  // 3. Stile per i campi readonly (iniettato nello shadow root:
  //    il CSS del tema non può raggiungerli da fuori)
  // ---------------------------------------------------------------------
  function injectReadonlyStyle(shadowRoot) {
    if (shadowRoot.getElementById('autofill-readonly-style')) return;
    const style = document.createElement('style');
    style.id = 'autofill-readonly-style';
    style.textContent = `
      #company_name[readonly],
      #first_name[readonly],
      #last_name[readonly],
      #phone[readonly] {
        background-color: #f5f5f5;
        cursor: not-allowed;
      }
    `;
    shadowRoot.appendChild(style);
  }

  // ---------------------------------------------------------------------
  // 4. Attesa dell'embed e del form dentro lo shadow root
  // ---------------------------------------------------------------------
  function waitForEmbed(callback) {
    const interval = setInterval(() => {
      const embed = document.querySelector('shopify-forms-embed');
      if (embed) {
        clearInterval(interval);
        callback(embed);
      }
    }, 500);
  }

  function waitForForm(embed, callback) {
    const shadowRoot = embed.shadowRoot;
    if (!shadowRoot) {
      setTimeout(() => waitForForm(embed, callback), 500);
      return;
    }
    const form = shadowRoot.querySelector("form[data-testid='form']");
    if (!form) {
      setTimeout(() => waitForForm(embed, callback), 500);
      return;
    }
    callback(shadowRoot);
  }

  // ---------------------------------------------------------------------
  // 5. Esecuzione
  // ---------------------------------------------------------------------
  waitForEmbed((embed) => {
    waitForForm(embed, (shadowRoot) => {
      injectReadonlyStyle(shadowRoot);
      fillField(shadowRoot, 'company_name', userData.company_name);
      fillField(shadowRoot, 'first_name', userData.first_name);
      fillField(shadowRoot, 'last_name', userData.last_name);
      fillField(shadowRoot, 'phone', userData.phone);
      console.log('[autofill] completato');
    });
  });
})();